import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Clock, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { submitContact } from "@/lib/api";

const JOST = "'Jost', system-ui, -apple-system, sans-serif";
const SERIF = "'Cormorant Garamond', Georgia, serif";
const INSTAGRAM_URL = "https://www.instagram.com/marrsingclause/";

const TOPICS = [
  "General Question",
  "Clarity Quiz",
  "Reveal & Alignment Report",
  "Partner Invitation Assistance",
  "Billing & Purchase Support",
  "Technical Assistance",
  "Media or Partnership Inquiry",
  "Speaking Engagement",
  "Something Else",
];

const FAQS = [
  {
    q: "How soon can I expect a response?",
    a: "Most messages are answered within 1 business day. Billing and account access issues are handled with priority.",
  },
  {
    q: "I purchased but haven't received access. What should I do?",
    a: "Check your inbox (including spam/promotions) for your welcome email, then reach out to us with the email used at checkout and we'll resolve it right away.",
  },
  {
    q: "Can my partner join after I complete my purchase?",
    a: "Yes. You'll be prompted to invite your partner inside your Reveal Space, and you can send that invite at any time after purchase.",
  },
  {
    q: "Is my information kept private?",
    a: "Yes. Your individual responses remain confidential until both partners complete their reflections and choose to view their shared Reveal together. Your information is protected and handled with care.",
  },
];

export default function Contact() {
  return (
    <div style={{ overflowX: "hidden", fontFamily: JOST }}>
      <Nav variant="light" />
      <ContactHero />
      <ContactBody />
      <FAQ />
      <Footer />
    </div>
  );
}

function ContactHero() {
  return (
    <section
      data-testid="contact-hero"
      style={{
        fontFamily: JOST,
        background: "linear-gradient(135deg,#FAF8F3,#EAF7FF)",
        color: "#1E1B16",
        padding: "150px 24px 72px",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        <p style={{ letterSpacing: "2px", textTransform: "uppercase", color: "#2F7EA8", fontWeight: 700, fontSize: 14, marginBottom: 16 }}>
          Marrsing Guidance
        </p>
        <h1 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: "clamp(38px, 6vw, 52px)", lineHeight: 1.12, margin: "0 0 20px", color: "#0E0A1F" }}>
          Let's Continue the Conversation
        </h1>
        <p style={{ fontSize: 18, lineHeight: 1.7, maxWidth: 640, margin: "0 auto 14px", color: "#3D3A35" }}>
          Whether you have a question about MarrsingClause®, your Reveal, your purchase, or how our Relationship Intelligence Alignment System™ works, we're here to help.
        </p>
        <p style={{ fontSize: 16, lineHeight: 1.7, maxWidth: 600, margin: "0 auto", color: "#5A5750" }}>
          Our team typically responds within one business day with thoughtful, personalized support.
        </p>
      </div>
    </section>
  );
}

function ContactBody() {
  return (
    <section style={{ fontFamily: JOST, background: "#FFFFFF", padding: "64px 24px" }}>
      <div className="mc-contact-grid" style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1.3fr 0.7fr", gap: 48, alignItems: "flex-start" }}>
        <ContactForm />
        <Sidebar />
      </div>
      <style>{`
        @media (max-width: 860px) {
          .mc-contact-grid { grid-template-columns: 1fr !important; }
          .mc-contact-namerow { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", topic: TOPICS[0], message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your full name.");
    if (!form.email.includes("@")) return toast.error("Please enter a valid email address.");
    if (!form.message.trim()) return toast.error("Please enter a message.");
    setSubmitting(true);
    try {
      await submitContact({ name: form.name.trim(), email: form.email.trim(), topic: form.topic, message: form.message.trim(), website: form.website });
      setDone(true);
      toast.success("Message sent — we'll be in touch within one business day.");
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("You've sent several messages already. Please try again in a little while.");
      } else {
        toast.error("Could not send right now. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="contact-form-card" style={{ background: "#F7FBFE", border: "1px solid #DCEEF8", borderRadius: 28, padding: 40 }}>
      <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 30, color: "#0E0A1F", margin: "0 0 8px" }}>How Can We Help?</h2>
      <p style={{ fontSize: 15, color: "#5A5750", margin: "0 0 28px" }}>Share what's on your mind, and our team will follow up personally.</p>

      {done ? (
        <div data-testid="contact-success" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "36px 20px", background: "#EAF7FF", border: "1px solid #CFE3EF", borderRadius: 18 }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#1F75A8", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
            <Check size={22} color="white" />
          </div>
          <h3 style={{ fontFamily: SERIF, fontSize: 24, color: "#0E0A1F", margin: "0 0 8px" }}>Message received.</h3>
          <p style={{ fontSize: 15, color: "#5A5750", margin: 0, lineHeight: 1.6 }}>
            Thank you for reaching out. A member of the Marrsing Clarity Team will respond within one business day. Check your inbox for a confirmation.
          </p>
        </div>
      ) : (
        <form data-testid="contact-form" onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Honeypot — hidden from real users; bots fill it and get silently dropped */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.website}
            onChange={set("website")}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
          />
          <div className="mc-contact-namerow" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Field label="Full Name">
              <input data-testid="contact-name" type="text" value={form.name} onChange={set("name")} placeholder="Your full name" required style={inputStyle} />
            </Field>
            <Field label="Email Address">
              <input data-testid="contact-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required style={inputStyle} />
            </Field>
          </div>

          <Field label="What can we help with?">
            <select data-testid="contact-topic" value={form.topic} onChange={set("topic")} style={inputStyle}>
              {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>

          <Field label="Message">
            <textarea data-testid="contact-message" rows={5} value={form.message} onChange={set("message")} placeholder="Tell us a little about what you need..." required style={{ ...inputStyle, resize: "vertical", minHeight: 120, paddingTop: 13 }} />
          </Field>

          <button data-testid="contact-submit" type="submit" disabled={submitting} style={{
            background: "#1F75A8", color: "white", border: "none", padding: "15px 28px",
            borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: submitting ? "default" : "pointer",
            boxShadow: "0 8px 20px rgba(31,117,168,.18)", marginTop: 4, opacity: submitting ? 0.7 : 1,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: JOST,
          }}>
            {submitting ? "Sending…" : "Send Message"} {!submitting && <ArrowRight size={16} />}
          </button>

          <p style={{ fontSize: 12.5, color: "#8A877E", margin: "6px 0 0", lineHeight: 1.6 }}>
            Your conversations with MarrsingClause® are treated with care and confidentiality.<br />
            We respect your privacy and never sell or share your personal information.
          </p>
        </form>
      )}
    </div>
  );
}

function Sidebar() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <SideCard icon={<Mail size={16} color="#1F75A8" />} title="Connect With Our Team">
        <p style={{ fontSize: 15, color: "#3D3A35", margin: "0 0 4px", lineHeight: 1.6 }}>
          Questions about MarrsingClause®, your Reveal, purchases, or partnerships?
        </p>
        <a data-testid="contact-email-link" href="mailto:clarity@marrsinglife.com" style={{ fontSize: 15, color: "#1F75A8", fontWeight: 600, textDecoration: "none" }}>
          clarity@marrsinglife.com
        </a>
      </SideCard>

      <SideCard icon={<Clock size={16} color="#1F75A8" />} title="Response Time">
        <p style={{ fontSize: 15, color: "#3D3A35", margin: 0, lineHeight: 1.65 }}>
          Most inquiries receive a thoughtful response within one business day. Billing and account access requests receive priority support.
        </p>
      </SideCard>

      <SideCard icon={<Sparkles size={16} color="#1F75A8" />} title="Stay Connected">
        <p style={{ fontSize: 14.5, color: "#3D3A35", margin: "0 0 14px", lineHeight: 1.65 }}>
          Explore relationship insights, clarity conversations, and practical guidance designed to strengthen intentional relationships.
        </p>
        <div style={{ display: "flex", gap: 14 }}>
          <a data-testid="contact-instagram" href={INSTAGRAM_URL} target="_blank" rel="noreferrer" style={{ color: "#1F75A8", fontWeight: 600, textDecoration: "none", fontSize: 14 }}>
            Instagram
          </a>
        </div>
      </SideCard>

      <div style={{ background: "#1F75A8", borderRadius: 24, padding: 28 }}>
        <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 19, color: "white", margin: "0 0 10px" }}>
          Ready for Greater Clarity?
        </h3>
        <p style={{ fontSize: 14, color: "rgba(255,255,255,.88)", margin: "0 0 16px", lineHeight: 1.65 }}>
          Take the MarrsingClause® Clarity Quiz and begin discovering the conversations that create stronger relationships.
        </p>
        <Link data-testid="contact-quiz-cta" to="/quiz" style={{
          display: "inline-block", background: "white", color: "#1F75A8", padding: "12px 22px",
          borderRadius: 999, textDecoration: "none", fontWeight: 600, fontSize: 14, marginBottom: 12,
        }}>
          Take the Clarity Quiz
        </Link>
        <div>
          <Link data-testid="contact-how-it-works" to="/about" style={{ color: "rgba(255,255,255,.85)", fontSize: 13, textDecoration: "underline" }}>
            Learn About Marrsing
          </Link>
        </div>
      </div>
    </div>
  );
}

function SideCard({ icon, title, children }) {
  return (
    <div style={{ background: "#EAF7FF", borderRadius: 24, padding: 28 }}>
      <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 19, color: "#0E0A1F", margin: "0 0 10px", display: "flex", alignItems: "center", gap: 8 }}>
        {icon}{title}
      </h3>
      {children}
    </div>
  );
}

function FAQ() {
  return (
    <section data-testid="contact-faq" style={{ fontFamily: JOST, background: "#FAF8F3", padding: "64px 24px" }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: "clamp(28px, 4vw, 34px)", color: "#0E0A1F", textAlign: "center", margin: "0 0 36px" }}>
          Frequently Asked Questions
        </h2>
        {FAQS.map((f, i) => (
          <div key={i} data-testid={`faq-${i}`} style={{ background: "white", borderRadius: 18, padding: "22px 26px", marginBottom: 14, border: "1px solid #EDE9DF" }}>
            <h3 style={{ fontSize: 17, color: "#1F75A8", margin: "0 0 8px", fontWeight: 600 }}>{f.q}</h3>
            <p style={{ fontSize: 15, color: "#5A5750", margin: 0, lineHeight: 1.65 }}>{f.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#1E1B16", marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "13px 14px",
  borderRadius: 12,
  border: "1px solid #CFE3EF",
  fontSize: 15,
  fontFamily: JOST,
  background: "#FFFFFF",
  color: "#1E1B16",
  boxSizing: "border-box",
  outline: "none",
};
